import { STORE_NAME } from '@intigral/constant';
import ResponseError from '@intigral/domain/lib/core/entities/errors/ResponseError';
import { toast } from '@intigral/shared';
import { set, get } from '@intigral/utils/lib/browser-storage/localStorage';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import user from '../../helpers/user';
import { selectProfileDone, switchProfileMutationFn } from '../../store/reducers/profileSlice';
import { __ } from '../../translations';
import { getUserInfo } from '../../utils/user';
import { useEffect } from 'react';
import { isGuestUser } from '../../config/guestMode';

const useSwitchProfileData = (isLiveLike?: boolean) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const B2B_localization = user.getUserSegment() === 'B2B' ? 'b2b_' : '';
    const isGuest = isGuestUser(getUserInfo());

    const { mutate, isPending: isProfileSwitching } = useMutation({
        mutationFn: switchProfileMutationFn,
        onSuccess: ({ result, profile }) => {
            const { identityToken, showWelcomeScreen, liveLikeAccessToken = null, liveLikeProfileId = null } = result;
            const currentStoredUserData = getUserInfo();
            set(STORE_NAME.USER, { ...currentStoredUserData, identityToken });

            // For kids profiles, ensure showWelcomeScreen is true regardless of server response
            // This ensures the gamification welcome modal appears for all kids profiles
            const shouldShowWelcomeScreen = profile.kidsMode ? true : showWelcomeScreen;

            const updatedProfile = {
                ...profile,
                date: Date.now(),
                showWelcomeScreen: shouldShowWelcomeScreen,
                liveLikeAccessToken,
                liveLikeProfileId,
            };
            set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
            dispatch(selectProfileDone(updatedProfile));

            queryClient.invalidateQueries({ queryKey: ['profiles'] });
        },
        onError: (error: ResponseError | { code?: string } | undefined) => {
            // switchProfileMutationFn throws error.data, so the payload may be either
            // ResponseError or the bare error data object.
            const errCode = (error as ResponseError)?.data?.code ?? (error as { code?: string })?.code;
            // The errorCode '9031' is for "Incorrect password", but for B2B the message should be "Incorrect account PIN"
            const localizedKey = errCode ? (errCode === '9031' ? B2B_localization : '') + `err_msg_${errCode}` : '';
            let localizedError = errCode ? __(localizedKey) : __('general_error_message');
            if (!errCode || localizedKey === localizedError)
                // If no localization found, fallback to general error message
                localizedError = __('general_error_message');
            toast(localizedError, 'error');
        },
    });

    useEffect(() => {
        if (isGuest) return;
        const localSelectedProfile = get(STORE_NAME.SELECTED_PROFILE);
        if (!localSelectedProfile || isLiveLike) return;
        mutate(localSelectedProfile);
    }, [isGuest, isLiveLike, mutate]);

    return { isProfileSwitching: isGuest ? false : isProfileSwitching };
};

export default useSwitchProfileData;
